import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { format, isSameWeek, isToday, isValid, differenceInCalendarDays } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { CheckIcon } from "@heroicons/react/24/outline";
import { deleteItemLog, markAsDoneActivity } from "../../api/ProjectApi";
import GroupedSectionHeader from "../../components/ui/GroupedSectionHeader";
import HoverActionsMenu from "../../components/HoverActionsMenu";
import ActivityAction from "../../actions/ActivityAction";
import { fromJS } from "immutable";
import { ChipAssignees } from "../../components/AssigneesSelect";
import ConfirmDialog from "../../dialog/ConfirmDialog";
import OctagonAlertIcon from "../../icons/OctagonAlertIcon";
import TriangleAlertIcon from "../../icons/TriangleAlertIcon";
import LayoutAction from "../../actions/LayoutAction";
import { renderItemIcon } from "../component/picker-shared";
import { SquareCheck } from "lucide-react";
import ListViewEmptyState from "./ListViewEmptyState";
import { isGmailContext } from "../../api/api";

type TaskData = {
  id: string;
  itemId: string;
  parentItemId: string;
  title: string;
  createdAt?: number;
  dueAt?: number;
  dueLabel?: string;
  overdue?: boolean;
  assignedMembers?: any[];
  completed?: boolean;
  item?: any;
};

const groupByTime = (tasks: TaskData[]) => {
  // Separate completed and incomplete tasks
  const completed: TaskData[] = [];
  const incomplete: TaskData[] = [];

  tasks.forEach((t) => {
    if (t.completed) {
      completed.push(t);
    } else {
      incomplete.push(t);
    }
  });

  // Sort incomplete tasks by createdAt (newest first)
  const sortedIncomplete = [...incomplete].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  // Sort completed tasks by createdAt (newest first)
  const sortedCompleted = [...completed].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  const today: TaskData[] = [];
  const thisWeek: TaskData[] = [];
  const months: Record<string, TaskData[]> = {};
  const now = new Date();
  const weekOptions = { weekStartsOn: 1 as const };

  // Group incomplete tasks by time
  sortedIncomplete.forEach((t) => {
    const dt = t.createdAt ? new Date(t.createdAt * 1000) : undefined;
    if (!dt) {
      thisWeek.push(t);
      return;
    }
    if (isToday(dt)) {
      today.push(t);
      return;
    }
    if (isSameWeek(dt, now, weekOptions)) {
      thisWeek.push(t);
      return;
    }
    const label = format(dt, "MMMM yyyy");
    (months[label] ||= []).push(t);
  });

  return { today, thisWeek, months, completed: sortedCompleted };
};

const GroupBlock = ({
  label,
  count,
  isCollapsed,
  onToggle,
  children,
}: {
  label: string;
  count: number;
  isCollapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) => {
  return (
    <div className="w-full">
      <GroupedSectionHeader
        label={label}
        count={count}
        isCollapsed={isCollapsed}
        onToggle={onToggle}
      />
      {!isCollapsed && <div className="mt-1">{children}</div>}
    </div>
  );
};

const TaskItem = ({
  task,
  onOpen,
  onToggleComplete,
  onDelete,
}: {
  task: TaskData;
  onOpen: (task: TaskData) => void;
  onToggleComplete: (task: TaskData) => void;
  onDelete: (task: TaskData) => void;
}) => {
  const navigate = useNavigate();
  const { projectId }: any = useParams();

  return (
    <div
      className={`relative group flex items-center justify-start gap-2 px-2 py-2 hover:bg-gray-50 transition-colors rounded-md ${task.completed ? "opacity-50" : ""}`}
    >
      {/* Checkbox */}
      <button
        type="button"
        onClick={() => onToggleComplete(task)}
        className={`w-4 h-4 rounded-sm border flex items-center justify-center cursor-pointer ${
          task.completed ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"
        }`}
        aria-pressed={!!task.completed}
      >
        {task.completed && <CheckIcon className="w-3 h-3 text-white" />}
      </button>

      {/* Title */}
      <button
        type="button"
        onClick={() => onOpen(task)}
        className="flex-1 truncate text-left text-sm font-medium text-gray-900 cursor-pointer"
        title={task.title}
      >
        {task.title}
      </button>

      {/* Due label */}
      <div className="flex-1">
        {task.dueLabel && (
          <div className={`text-xs ${task.overdue ? "text-red-500" : "text-blue-500"}`}>
            {task.dueLabel}
          </div>
        )}
      </div>

      {/* Item */}
      <div
        className="flex-2 flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer"
        onClick={() => {
          navigate(`/workspaces/${projectId}/items/${task.itemId}?type=${task.item?.type}`);
        }}
      >
        {renderItemIcon(task.item ?? {}, task.item?.type)}
        {task.item?.label}
      </div>

      {/* Assignees */}
      <div className="flex-2">
        <ChipAssignees
          type="INLINE"
          assignedMembers={fromJS(task.assignedMembers)}
          onlyAvatar={false}
        />
      </div>

      {/* Hover Actions Menu */}
      <HoverActionsMenu onOpen={() => onOpen(task)} onDelete={() => onDelete(task)} />
    </div>
  );
};

const ListViewTask = ({ items, onSearchTasks }: any) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId }: any = useParams();

  const project = useSelector((state: any) => state.projectReducer.get("project"));
  const upcomingFilters =
    useSelector((state: any) => state.activityReducer.get("upcomingFilters")) || fromJS({});

  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const computeDueLabel = (start: any, startTime: string) => {
    if (!start) return "";
    let dt: Date;
    // Handle both timestamp (number) and date string
    if (typeof start === "number") {
      // If it's a timestamp in milliseconds, use it directly
      dt = new Date(start);
    } else if (typeof start === "string") {
      // If it's a date string, combine with startTime if provided
      const dtStr = `${start}${startTime ? `T${startTime}` : ""}`;
      dt = new Date(dtStr);
    } else {
      return "";
    }
    if (!isValid(dt)) return "";
    const days = differenceInCalendarDays(dt, new Date());
    if (days === 0) return "@Due today";
    if (days > 0) return `@Due in ${days} day${days > 1 ? "s" : ""}`;
    const ago = Math.abs(days);
    return `@Due ${ago} day${ago > 1 ? "s" : ""} ago`;
  };

  const groups = useMemo(() => {
    const tasks = items.map((item) => {
      const createdAt = item.getIn(["event", "createdAt"]);
      const summary = item.getIn(["event", "data", "summary"]) || "";
      const timeZone = item.getIn(["event", "data", "timeZone"]);
      let start = item.getIn(["event", "data", "start"]);
      if (typeof start === "number") {
        start = formatInTimeZone(
          start,
          timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          "yyyy-MM-dd"
        );
      }
      const startTime = item.getIn(["event", "data", "startTime"]) || "";
      const dueLabel = computeDueLabel(start, startTime);
      const assignees = item.getIn(["event", "data", "assignees"])?.toJS() || [];
      const members = project.get("members")?.toJS() || [];
      const assignedMembers = members.filter((item) => assignees.includes(item.accountId));
      const done = item.getIn(["event", "data", "done"]) || false;
      const itemData = item.getIn(["event", "data", "item"]) || fromJS({});
      return {
        id: item.get("id"),
        itemId: item.get("itemId"),
        parentItemId: itemData.get("value") || itemData.get("id") || item.get("itemId"),
        title: summary,
        createdAt,
        dueAt: typeof start === "number" ? Math.floor(start / 1000) : undefined, // Convert to seconds epoch if timestamp
        dueLabel,
        overdue: dueLabel.includes("ago"),
        assignedMembers,
        completed: done,
        item: itemData.toJS(),
      };
    });
    return groupByTime(tasks);
  }, [items, project]);

  const handleOpen = (task: TaskData) => {
    dispatch(ActivityAction.showActivityDialog(task.itemId, task.id));
  };

  const handleToggleComplete = (task: TaskData) => {
    markAsDoneActivity(task.itemId, task.id, !task.completed).then(() => {
      onSearchTasks({ projectId });
      if (isGmailContext()) {
        dispatch(ActivityAction.searchUpcomingChrome(projectId, upcomingFilters));
      }
    });
  };

  const handleDeleteClick = (task: TaskData) => {
    setShowConfirm({
      show: true,
      title: t("Delete task"),
      description: t("Are you sure you want to remove this task?"),
      alertIcon: <TriangleAlertIcon />,
      dangerBtn: true,
      type: "DELETE",
      data: task,
    });
  };

  const handleSubmitConfirm = () => {
    if (showConfirm.type === "DELETE") {
      const { parentItemId, id } = showConfirm.data;
      if (!parentItemId || !id) {
        setShowConfirm({ show: false, title: "", description: "" });
        return;
      }
      deleteItemLog(parentItemId, id)
        .then(() => {
          onSearchTasks({ projectId });
          dispatch(
            LayoutAction.setNotification({
              title: t("Delete task"),
              type: "success",
              message: t("Delete task successfully!"),
            })
          );
        })
        .catch(() => {
          dispatch(
            LayoutAction.setNotification({
              title: t("Delete task"),
              type: "error",
              message: t("Failed to delete task"),
            })
          );
        });
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const handleGroupToggle = (label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  return (
    <div className="w-full flex flex-col gap-3">
      {items.size > 0 && (
        <div className="w-full">
          <div className="flex items-center justify-start gap-2 py-2 bg-gray-100">
            <div className="w-4 h-4"></div>
            <div className="flex-1">{t("Task")}</div>
            <div className="flex-1">{t("Due date")}</div>
            <div className="flex-2">{t("Item")}</div>
            <div className="flex-2">{t("Assigned to")}</div>
          </div>
        </div>
      )}

      {/* Incomplete tasks grouped by time */}
      {groups.today.length > 0 && (
        <GroupBlock
          label="Today"
          count={groups.today.length}
          isCollapsed={collapsedGroups.has("Today")}
          onToggle={() => handleGroupToggle("Today")}
        >
          {groups.today.map((task) => (
            <TaskItem
              key={task.id}
              task={task}
              onOpen={handleOpen}
              onToggleComplete={handleToggleComplete}
              onDelete={handleDeleteClick}
            />
          ))}
        </GroupBlock>
      )}

      {groups.thisWeek.length > 0 && (
        <GroupBlock
          label="This week"
          count={groups.thisWeek.length}
          isCollapsed={collapsedGroups.has("This week")}
          onToggle={() => handleGroupToggle("This week")}
        >
          {groups.thisWeek.map((task) => (
            <TaskItem
              key={task.id}
              task={task}
              onOpen={handleOpen}
              onToggleComplete={handleToggleComplete}
              onDelete={handleDeleteClick}
            />
          ))}
        </GroupBlock>
      )}

      {Object.entries(groups.months).map(([label, list]) => (
        <GroupBlock
          key={label}
          label={label}
          count={list.length}
          isCollapsed={collapsedGroups.has(label)}
          onToggle={() => handleGroupToggle(label)}
        >
          {list.map((task) => (
            <TaskItem
              key={task.id}
              task={task}
              onOpen={handleOpen}
              onToggleComplete={handleToggleComplete}
              onDelete={handleDeleteClick}
            />
          ))}
        </GroupBlock>
      ))}

      {/* Completed tasks group */}
      {groups.completed.length > 0 && (
        <GroupBlock
          label="Completed"
          count={groups.completed.length}
          isCollapsed={collapsedGroups.has("Completed")}
          onToggle={() => handleGroupToggle("Completed")}
        >
          {groups.completed.map((task) => (
            <TaskItem
              key={task.id}
              task={task}
              onOpen={handleOpen}
              onToggleComplete={handleToggleComplete}
              onDelete={handleDeleteClick}
            />
          ))}
        </GroupBlock>
      )}

      {items.size === 0 && (
        <ListViewEmptyState
          icon={SquareCheck}
          title="Tasks"
          description="No tasks yet! Create your first task to get started."
          actionLabel="New task"
          onAction={() =>
            dispatch(
              ActivityAction.showActivityDialog(null, null, {
                activityType: "task",
                summary: "",
              })
            )
          }
        />
      )}

      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
        dangerBtn={showConfirm.dangerBtn}
        onSubmit={handleSubmitConfirm}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </div>
  );
};

export default ListViewTask;
